import React, { useEffect, useRef, useState } from 'react';

/**
 * AI 시네마그래프 생성 모달 — 이미지 1장 + 프롬프트 → POST /aivideo/generate →
 * GET /aivideo/jobs/:hash 폴링으로 진행률 표시. 완료 시 onSuccess()로 목록 새로고침.
 * 창을 닫아도 서버는 백그라운드로 계속 생성(페이지의 목록 자동 새로고침으로 나중에 반영).
 */

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type Status = 'idle' | 'submitting' | 'queued' | 'submitted' | 'processing' | 'downloading' | 'done' | 'failed';

const BUSY: Status[] = ['submitting', 'queued', 'submitted', 'processing', 'downloading'];

const statusLabel = (s: Status): string => {
  switch (s) {
    case 'submitting': return '요청 중';
    case 'queued': case 'submitted': return '대기 중';
    case 'processing': return '생성 중';
    case 'downloading': return '내려받는 중';
    default: return '진행 중';
  }
};

const AiVideoModal: React.FC<Props> = ({ isOpen, onClose, onSuccess }) => {
  const [config, setConfig] = useState<{ defaultDurationSec: number; maxDurationSec: number; maxImageBytes: number } | null>(null);
  const [image, setImage] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [prompt, setPrompt] = useState('subtle, localized, seamless looping motion — keep the composition static');
  const [durationSec, setDurationSec] = useState(5);
  const [status, setStatus] = useState<Status>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const stopPoll = () => { if (pollRef.current) { window.clearInterval(pollRef.current); pollRef.current = null; } };

  useEffect(() => {
    if (!isOpen) return;
    fetch('/aivideo/config', { credentials: 'include' })
      .then(r => r.json())
      .then(j => { if (j.success) { setConfig(j.data); setDurationSec(j.data.defaultDurationSec); } })
      .catch(() => {});
    return stopPoll;
  }, [isOpen]);

  const reset = () => {
    setImage(null); setPreview(null); setStatus('idle'); setProgress(0); setError(null);
    stopPoll();
  };
  const handleClose = () => { reset(); onClose(); };

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setError(null);
    if (!f) return;
    if (config && f.size > config.maxImageBytes) {
      setError(`이미지가 너무 큽니다 (최대 ${Math.round(config.maxImageBytes / 1024 / 1024)}MB)`);
      return;
    }
    setImage(f);
    const reader = new FileReader();
    reader.onload = ev => setPreview(ev.target?.result as string);
    reader.readAsDataURL(f);
  };

  const poll = (hash: string) => {
    pollRef.current = window.setInterval(async () => {
      try {
        const r = await fetch(`/aivideo/jobs/${hash}`, { credentials: 'include' });
        const j = await r.json();
        if (!j.success) return;
        setStatus(j.data.status);
        setProgress(j.data.progress ?? 0);
        if (j.data.status === 'done') { stopPoll(); onSuccess(); }
        else if (j.data.status === 'failed') { stopPoll(); setError(j.data.error || '생성 실패'); }
      } catch { /* 일시 오류는 다음 폴에서 재시도 */ }
    }, 3000);
  };

  const handleGenerate = async () => {
    if (!image) { setError('이미지를 선택하세요.'); return; }
    if (!prompt.trim()) { setError('프롬프트를 입력하세요.'); return; }
    setError(null); setStatus('submitting');
    const fd = new FormData();
    fd.append('image', image);
    fd.append('prompt', prompt.trim());
    fd.append('durationSec', String(durationSec));
    try {
      const r = await fetch('/aivideo/generate', { method: 'POST', credentials: 'include', body: fd });
      const j = await r.json();
      if (!j.success) { setStatus('failed'); setError(j.error || '생성 시작 실패'); return; }
      setStatus('queued');
      poll(j.data.jobHash);
    } catch (e: any) {
      setStatus('failed'); setError(e?.message || '생성 시작 실패');
    }
  };

  if (!isOpen) return null;
  const busy = BUSY.includes(status);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}>
      <div className="bg-white p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">🎬 AI 영상 생성</h2>
          <button onClick={handleClose} className="text-gray-500 hover:text-gray-700 text-2xl leading-none">&times;</button>
        </div>
        <p className="text-xs text-gray-500 mb-4">이미지 한 장 + 프롬프트로 짧은 시네마그래프 영상을 생성합니다. 완료되면 내 영상 목록에 추가됩니다.</p>

        {status === 'done' ? (
          <div className="text-center py-6">
            <div className="text-green-600 font-medium mb-2">✅ 생성 완료 — 내 영상 목록에 추가되었습니다.</div>
            <div className="text-xs text-gray-500 mb-4">게재 준비가 끝나면 내 화면에 올릴 수 있습니다.</div>
            <button onClick={handleClose} className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2">닫기</button>
          </div>
        ) : (
          <>
            <label className="block text-sm font-medium mb-1">원본 이미지</label>
            <div onClick={() => !busy && fileRef.current?.click()}
              className={`border-2 border-dashed p-4 mb-3 text-center ${busy ? 'border-gray-200' : 'border-gray-300 hover:border-blue-400 cursor-pointer'}`}>
              {preview
                ? <img src={preview} alt="" className="max-h-40 mx-auto" />
                : <span className="text-sm text-gray-400">클릭해서 이미지 선택</span>}
              <input ref={fileRef} type="file" accept="image/*" onChange={onPick} className="hidden" disabled={busy} />
            </div>

            <label className="block text-sm font-medium mb-1">프롬프트</label>
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} disabled={busy} rows={3}
              className="w-full border border-gray-300 px-2 py-1 mb-3 text-sm" />

            <label className="block text-sm font-medium mb-1">길이(초)</label>
            <select value={durationSec} onChange={(e) => setDurationSec(Number(e.target.value))} disabled={busy}
              className="border border-gray-300 px-2 py-1 mb-4">
              <option value={5}>5초</option>
              <option value={10}>10초</option>
            </select>

            {error && <div className="text-sm text-red-600 mb-3">{error}</div>}

            {busy ? (
              <div className="mb-1">
                <div className="text-sm text-gray-600 mb-1">{statusLabel(status)} … {progress}%</div>
                <div className="w-full bg-gray-200 h-2"><div className="bg-blue-500 h-2 transition-all" style={{ width: `${progress}%` }} /></div>
                <div className="text-[11px] text-gray-400 mt-1">생성에 수십 초~수 분 걸립니다. 창을 닫아도 백그라운드로 계속되며 목록에 자동 반영됩니다.</div>
              </div>
            ) : (
              <div className="flex justify-end gap-2">
                <button onClick={handleClose} className="bg-gray-200 hover:bg-gray-300 px-4 py-2">취소</button>
                <button onClick={handleGenerate} className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2">생성 시작</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AiVideoModal;
